import { useMemo, useState } from "react";
import {
  Accordion,
  Button,
  Grid,
  GridCol,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconChevronsDown,
  IconChevronsUp,
  IconClock,
} from "@tabler/icons-react";
import { openConfirmModal } from "@mantine/modals";
import { Navigate, useNavigate, useOutletContext } from "react-router-dom";
import { isValidPatient, Product } from "@jasper/client-models";
import {
  isValidProduct,
  useProductDescription,
} from "@jasper/product-workflows";
import { useOrderConfiguration } from "@jasper/use-cases";
import { getDentistName } from "../patient.utils";
import {
  DELIVERY_OPTION_LABELS,
  formatDeliveryDate,
  getDeliveryOption,
} from "./delivery.utils";
import { DeliveryDateOptions } from "./DeliveryDateOptions";
import { OrderCommentField } from "./OrderCommentField";
import { OrderFilesField } from "./OrderFilesField";
import { PatientSection } from "./PatientSection";
import { PhoneUploadQrCode } from "./PhoneUploadQrCode";
import { ProductsSection } from "./ProductsSection";
import { ReadyForLabPanel } from "./ReadyForLabPanel";
import { isImageFile, pluralize } from "./review.utils";
import {
  ALL_REVIEW_SECTIONS,
  CARD_BORDER_STYLE,
  REVIEW_SECTIONS,
  ReviewCard,
  ReviewSection,
} from "./ReviewCard";
import { OrderStepsEnum } from "../../OrderStepper";
import { useDeliveryDate } from "./useDeliveryDate";
import { useOrderSubmission } from "./useOrderSubmission";
import { useCrownSplinting } from "./useCrownSplinting";

const DEFAULT_OPEN_SECTIONS: ReviewSection[] = [
  REVIEW_SECTIONS.patient,
  REVIEW_SECTIONS.products,
  REVIEW_SECTIONS.commentAndDelivery,
];

export const OrderSummaryV2 = () => {
  const { getProductsCountLabel, getProductTypeLabel } =
    useProductDescription();
  const { orderId } = useOutletContext<{ orderId: string }>();
  const navigate = useNavigate();

  const orderConfiguration = useOrderConfiguration({ orderId });
  const {
    order,
    isLoading,
    error,
    confirmOrder,
    removeOrderProduct,
    materialAndShadeCatalog,
  } = orderConfiguration;

  // Delivery date state lives here, not in its section, because the "Prêt pour le laboratoire" panel also needs it to send the order
  const {
    deliveryDate,
    setDeliveryDate,
    estimatedOptimalDeliveryDate,
    estimatedRushDeliveryDate,
    hasEstimationFailed,
    bankHolidays,
    handleMonthChange,
  } = useDeliveryDate(orderConfiguration);

  const {
    isDesignRequestEnabled,
    isDesignRequested,
    toggleDesignRequest,
    changeDeliveryDate,
    isDeliveryDateTooSoon,
    isSending,
    send,
  } = useOrderSubmission({
    orderId,
    confirmOrder,
    deliveryDate,
    setDeliveryDate,
    estimatedOptimalDeliveryDate,
  });

  const { splintCrowns, lastSplintedProductId, undoLastSplint } =
    useCrownSplinting(orderConfiguration);

  // Open sections, controlled so the header button can fold or unfold them all at once
  const [openSections, setOpenSections] = useState<string[]>(
    DEFAULT_OPEN_SECTIONS
  );
  const isAnySectionOpen = openSections.length > 0;

  // Holds every product only when all of them are fully configured (empty otherwise),
  // so a length mismatch with order.products means at least one product is still incomplete
  const products: Product[] = useMemo(() => {
    if (order?.products?.every(product => isValidProduct(product))) {
      return order.products as Product[];
    }

    return [];
  }, [order]);

  // The material and shade catalog comes with the order: both are there or neither
  if (isLoading || error || !order || !materialAndShadeCatalog) {
    return null;
  }

  // Same guards as V1: an order can't be reviewed with missing information,
  // so send the user back to the first step that still needs to be completed.
  if (!isValidPatient(order.patient)) {
    return <Navigate to={`../${OrderStepsEnum.orderInfo}`} replace />;
  }

  if (products.length !== order.products?.length) {
    const firstInvalidProduct = order.products?.find(
      product => !product || !isValidProduct(product)
    );

    return (
      <Navigate
        to={`../products/${firstInvalidProduct?.id ?? "new"}`}
        replace
      />
    );
  }

  const confirmProductDeletion = (product: Product) =>
    openConfirmModal({
      title: "Supprimer la prothèse",
      children: `Vous êtes sur le point de supprimer de votre commande une prothèse de type ${getProductTypeLabel(product)}. Êtes-vous sûr de vouloir continuer ?`,
      labels: { confirm: "Supprimer", cancel: "Annuler" },
      confirmProps: { color: "red" },
      onConfirm: () => removeOrderProduct(product.id),
    });

  // One-line recaps, shared by the folded section headers and the "Prêt pour le laboratoire" panel
  const dentistName = getDentistName(order.dentist);
  const patientName = `${order.patient.firstName} ${order.patient.lastName}`;
  const deliverySummary =
    deliveryDate &&
    `${
      DELIVERY_OPTION_LABELS[
        getDeliveryOption({
          deliveryDate,
          estimatedOptimalDeliveryDate,
          estimatedRushDeliveryDate,
        })
      ]
    }, ${formatDeliveryDate(deliveryDate)}`;

  // e.g. "2 photos · 1 fichier"
  const files = order?.files ?? [];
  const photosCount = files.filter(file => isImageFile(file.filename)).length;
  const otherFilesCount = files.length - photosCount;
  const filesSummary =
    files.length === 0
      ? "Aucun fichier"
      : [
          photosCount > 0 && pluralize(photosCount, "photo", "photos"),
          otherFilesCount > 0 &&
            pluralize(otherFilesCount, "fichier", "fichiers"),
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    <Stack maw={1180} mx="auto" py="s10" gap="md">
      {/* Page header: title, subtitle and the button folding every section at once */}
      <Stack gap={0}>
        <Group justify="space-between" align="flex-end" gap="xs">
          <Title variant="display" size="lg">
            Un dernier{" "}
            <Text span inherit variant="accent">
              regard
            </Text>
            .
          </Title>
          <Button
            variant="default"
            radius="xl"
            leftSection={
              isAnySectionOpen ? (
                <IconChevronsUp size={16} />
              ) : (
                <IconChevronsDown size={16} />
              )
            }
            onClick={() =>
              setOpenSections(isAnySectionOpen ? [] : ALL_REVIEW_SECTIONS)
            }
          >
            {isAnySectionOpen ? "Tout replier" : "Tout déplier"}
          </Button>
        </Group>
        <Text>Vérifiez avant l&#39;envoi au laboratoire.</Text>
      </Stack>

      <Grid gap="md">
        {/* Left column: the review sections, each one can be folded independently */}
        <GridCol span="auto">
          <Accordion
            order={3}
            variant="unstyled"
            chevronPosition="left"
            multiple
            value={openSections}
            onChange={setOpenSections}
            styles={{
              root: {
                display: "flex",
                flexDirection: "column",
                gap: "var(--mantine-spacing-lg)",
              },
            }}
          >
            <ReviewCard
              value={REVIEW_SECTIONS.patient}
              onEdit={() => navigate(`../${OrderStepsEnum.orderInfo}`)}
              index={1}
              title="Patient & praticien"
              summary={`${patientName} · ${dentistName}`}
            >
              <PatientSection patient={order.patient} dentist={order.dentist} />
            </ReviewCard>

            <ReviewCard
              value={REVIEW_SECTIONS.products}
              index={2}
              title="Prothèses"
              count={products.length}
              summary={getProductsCountLabel(products.length)}
            >
              <ProductsSection
                products={products}
                materialAndShadeCatalog={materialAndShadeCatalog}
                onEdit={product => navigate(`../products/${product.id}`)}
                onDelete={confirmProductDeletion}
                onAdd={() => navigate("../products/add")}
                onSplint={splintCrowns}
                lastSplintedProductId={lastSplintedProductId}
                onUndoLastSplint={undoLastSplint}
              />
            </ReviewCard>

            <ReviewCard
              value={REVIEW_SECTIONS.files}
              index={3}
              title="Empreintes, photos & fichiers"
              summary={filesSummary}
            >
              {/* TODO: "Modifier" action in the header once the impressions step is part of the order flow (files are edited in place for now) */}
              <Stack gap="md">
                {/* "Upcoming" variant, as in the "Prêt pour le laboratoire" panel: the impressions step is not part of the order flow yet.
                    TODO: show the received scan (e.g. "Scan intra-oral · 3 fichiers · 3Shape") once it is */}
                <Group gap="xs" c="dimmed">
                  <ThemeIcon
                    variant="transparent"
                    color="ink"
                    size="sm"
                    aria-hidden
                  >
                    <IconClock />
                  </ThemeIcon>
                  <Title order={4} c="inherit">
                    Scan intra-oral
                  </Title>
                </Group>
                <SimpleGrid cols={2} spacing="md">
                  <PhoneUploadQrCode orderId={orderId} />
                  <OrderFilesField orderId={orderId} />
                </SimpleGrid>
              </Stack>
            </ReviewCard>

            <ReviewCard
              value={REVIEW_SECTIONS.commentAndDelivery}
              index={4}
              title="Commentaire & livraison"
              summary={deliverySummary}
            >
              <Stack gap="lg">
                <OrderCommentField orderId={orderId} />
                <DeliveryDateOptions
                  deliveryDate={deliveryDate}
                  onDeliveryDateChange={changeDeliveryDate}
                  estimatedOptimalDeliveryDate={estimatedOptimalDeliveryDate}
                  estimatedRushDeliveryDate={estimatedRushDeliveryDate}
                  hasEstimationFailed={hasEstimationFailed}
                  bankHolidays={bankHolidays}
                  onMonthChange={handleMonthChange}
                  error={
                    isDeliveryDateTooSoon
                      ? "La date de livraison est trop proche. Veuillez en choisir une autre."
                      : undefined
                  }
                />
              </Stack>
            </ReviewCard>
          </Accordion>
        </GridCol>

        {/* Right column: sticky panel summing up the order and sending it to the lab */}
        <GridCol span={3}>
          <Paper
            component="aside"
            withBorder
            radius="lg"
            p="xl"
            style={CARD_BORDER_STYLE}
            pos="sticky"
            top="var(--mantine-spacing-xl)"
          >
            <ReadyForLabPanel
              patientName={patientName}
              productsCount={products.length}
              deliveryText={deliverySummary}
              showDesignRequest={isDesignRequestEnabled}
              isDesignRequested={isDesignRequested}
              onDesignRequestedToggle={toggleDesignRequest}
              onSend={send}
              isSending={isSending}
            />
          </Paper>
        </GridCol>
      </Grid>
    </Stack>
  );
};
